import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../ui/dialog";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  avatar?: ReactNode;
  children?: ReactNode;
  primaryAction?: {
    label: string;
    onClick: () => void;
    isDisabled?: boolean;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    isDisabled?: boolean;
  };
  tertiaryAction?: {
    label: string;
    onClick: () => void;
    isDisabled?: boolean;
  };
  size?: "sm" | "md" | "lg" | "full";
}

export function Modal({
  isOpen,
  onClose,
  title,
  avatar,
  children,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  size = "sm",
}: ModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          className={cn("p-6 flex flex-col gap-6", {
            "w-[calc(100vw-48px)] max-w-[500px] rounded-8": size === "sm",
            "w-[calc(100vw-48px)] max-w-[800px] rounded-8": size === "md",
            "w-[calc(100vw-48px)] max-w-[1080px] rounded-8": size === "lg",
            "w-screen h-screen !m-0 !p-0 !translate-x-0 !translate-y-0 !top-0 !left-0 !max-w-none":
              size === "full",
          })}
        >
          {size === "full" ? (
            <div className="p-6 flex flex-col gap-6 h-full">
              <DialogHeader className="flex flex-row items-center gap-2">
                {avatar && <div className="flex">{avatar}</div>}
                <DialogTitle className="font-normal text-20 leading-[1.2em] text-neutral-800">
                  {title}
                </DialogTitle>
              </DialogHeader>

              <div className="flex flex-col gap-6 flex-1 overflow-auto">
                {children}
              </div>

              <ModalFooter
                primaryAction={primaryAction}
                secondaryAction={secondaryAction}
                tertiaryAction={tertiaryAction}
              />
            </div>
          ) : (
            <>
              <DialogHeader className="flex flex-row items-center gap-2">
                {avatar && <div className="flex">{avatar}</div>}
                <DialogTitle className="font-normal text-20 leading-[1.2em] text-neutral-800">
                  {title}
                </DialogTitle>
              </DialogHeader>

              <div className="flex flex-col gap-6">{children}</div>

              <ModalFooter
                primaryAction={primaryAction}
                secondaryAction={secondaryAction}
                tertiaryAction={tertiaryAction}
              />
            </>
          )}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

function ModalFooter({
  primaryAction,
  secondaryAction,
  tertiaryAction,
}: Pick<ModalProps, "primaryAction" | "secondaryAction" | "tertiaryAction">) {
  const hasActions = primaryAction || secondaryAction || tertiaryAction;
  if (!hasActions) return null;

  return (
    <DialogFooter className="flex flex-row justify-between items-center space-x-0 w-full">
      <div>
        {tertiaryAction && (
          <Button 
            variant="ghost" 
            onClick={tertiaryAction.onClick}
            disabled={tertiaryAction.isDisabled ?? false}
          >
            {tertiaryAction.label}
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {secondaryAction && (
          <Button 
            variant="tertiary" 
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.isDisabled ?? false}
          >
            {secondaryAction.label}
          </Button>
        )}
        {primaryAction && (
          <Button 
            variant="primary" 
            onClick={primaryAction.onClick}
            disabled={primaryAction.isDisabled ?? false}
          >
            {primaryAction.label}
          </Button>
        )}
      </div>
    </DialogFooter>
  );
}

/**
 * have no clue what the design team wants exactly, the designs are confusing at best and hostile if I'm allowed to be candid.
 * So I've kept this here in case the guideline is actually to have this kind of layout. I'll delete this or integrate in main component this when I clarify.
 *
 * Until then in case its needed you can import this and pass this as childrenA
 *
 */
export function ModalContent({
  text,
  contentArea,
}: {
  text?: string;
  contentArea?: ReactNode;
}) {
  return (
    <>
      {text && (
        <p className="text-16 leading-[1.5em] text-neutral-800">{text}</p>
      )}
      {contentArea && (
        <div className="w-full flex justify-center items-center">
          {contentArea}
        </div>
      )}
    </>
  );
}
